import React, { useEffect, useState } from 'react';
import { Box, Chip, TextField, Avatar } from '@mui/material';
import { Cancel } from '@mui/icons-material';
import {
    blue, pink, green, indigo, purple, amber,
    grey, cyan, orange, lime, blueGrey, deepPurple,
    deepOrange, teal, red, lightBlue, yellow, brown, lightGreen
} from '@mui/material/colors';

const avatarColors = [
    pink[600], green[600], blue[700], amber[600], indigo[600], purple[600],
    grey[600], cyan[600], orange[600], lime[600],
    blueGrey[600], deepPurple[600], deepOrange[600], teal[600], red[600],
    lightBlue[600], yellow[600], brown[600], lightGreen[600],
];

interface IProps {
    label: string;
    placeholder?: string;
    separator: string;
    value: string;
    onChange: (value: string) => void;
    onErrorChange: (error: string | null) => void;
    validationError?: string;
    resetTrigger?: number;
}

/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Dec/01/2024
 */
export default function EmailChipsComponent({
    label, placeholder, resetTrigger,
    separator, value, onChange, onErrorChange, validationError
}: IProps) {

    const [emails, setEmails] = useState<string[]>(value.split(separator).filter(Boolean)); // Valid email addresses
    const [inputValue, setInputValue] = useState<string>(''); // Input value
    const [errorMessage, setErrorMessage] = useState<string | null>(validationError || null); // Error message

    useEffect(() => {
        setEmails(value.split(separator).filter(Boolean));
        setErrorMessage(null);
        setInputValue('');
    }, [resetTrigger])

    useEffect(() => {
        onErrorChange(errorMessage)
    }, [errorMessage])

    const updateEmails = (newEmails: string[]) => {
        setEmails(newEmails);
        onChange(newEmails.join(separator)); // Emit updated string
    };

    useEffect(() => {
        setErrorMessage(validationError || null); // Sync validation errors from react-hook-form
    }, [validationError]);

    useEffect(() => {
        const newEmails = value.split(separator).filter(Boolean);
        setEmails(newEmails);
    }, [value, separator]);

    // Validate email format
    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault(); // Prevent default paste behavior
        const pastedText = event.clipboardData.getData('Text'); // Get pasted text

        // Split by delimiters, trim whitespace, and process emails
        const allEmails = pastedText.trim()
            .split(/[\s,;|]+/) // Split by spaces, commas, semicolons, or pipe
            .map(email => email.trim()) // Trim spaces

        const validEmails = allEmails.filter(email => isValidEmail(email)); // Extract valid emails
        const invalidEmails = allEmails.filter(email => !isValidEmail(email)); // Extract invalid emails

        // Remove duplicates from valid emails and already added emails
        const uniqueValidEmails = validEmails.filter(email => !emails.includes(email));

        // Update the state with valid emails and show invalid ones in the input field
        if (uniqueValidEmails.length > 0) {
            updateEmails([...emails, ...uniqueValidEmails]); // Add valid emails as chips
        }

        // Set the input value to invalid emails, joined back as a string
        const invalidCharacters = invalidEmails.join(', ').trim();
        setInputValue(invalidCharacters);

        // Set error if there are invalid emails
        if (invalidCharacters.length > 0) {
            setErrorMessage('Some emails are invalid. Please correct or remove them.');
        } else {
            setErrorMessage(null); // Clear error if no invalid emails
        }
    };

    const handleOnBlur = () => {
        if (inputValue !== '') {
            validateInput(false);
        }
    }

    const handleInputChange = (value: string) => {
        setInputValue(value);
        if (!value.trim()) {
            setErrorMessage(null); // Clear error if input is empty
        }
    };

    const validateInput = (clearInput: boolean = true) => {
        const trimmedValue = inputValue.trim();
        if (isValidEmail(trimmedValue)) {
            if (!emails.includes(trimmedValue)) {
                updateEmails([...emails, trimmedValue]);
                setInputValue(''); // Clear input if specified
                setErrorMessage(null); // Clear error
            } else {
                setErrorMessage('Duplicate email address');
            }
        } else if (trimmedValue) {
            setErrorMessage('Invalid email address');
        } else {
            setErrorMessage(null); // Clear error if input is empty
        }

        if (clearInput) {
            setInputValue(''); // Clear input if specified
        }
    };

    // Handle Enter, Comma, Semicolon, Pipe, or Space to add an email
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' || event.key === ',' || event.key === ';' || event.key === '|' || event.key === ' ') {
            event.preventDefault(); // Prevent default behavior
            handleAddEmail();
        } else if (event.key === 'Escape') {
            setInputValue(''); // Clear input on Escape key press
            setErrorMessage(null); // clear the error as well
        }
    };

    // Add individual email
    const handleAddEmail = () => {
        validateInput();
        setInputValue(''); // Clear input
    };

    // Remove email
    const handleDeleteEmail = (emailToDelete: string) => {
        updateEmails(emails.filter(email => email !== emailToDelete));
    };

    const extractAvatarInitials = (email: string): string => {
        if (!email || typeof email !== "string") {
            return "??"; // Default for invalid email
        }

        const [localPart] = email.split("@"); // Extract the local part of the email
        if (!localPart) {
            return "??"; // Default if no local part
        }

        const parts = localPart.split(/[-._]/); // Split by -, ., or _
        const meaningfulParts = parts.filter(part => part.length > 0); // Ignore empty parts

        // Handle cases with no separators
        if (meaningfulParts.length === 1) {
            const part = meaningfulParts[0];
            const letters = part.replace(/[^a-zA-Z]/g, ""); // Remove non-alphabetic characters
            if (letters.length >= 2) {
                return letters.slice(0, 2).toUpperCase(); // Use the first two letters
            }
            return letters.toUpperCase() || "??"; // Single letter or fallback
        }

        // Extract initials from meaningful parts
        const initials = meaningfulParts
            .map(part => part[0].toUpperCase()) // Get the first letter of each part
            .slice(0, 2); // Limit to the first two initials

        return initials.join("") || "??"; // Join and return, fallback to ??
    }
    return (
        <Box sx={{ width: '100%' }}>
            <TextField
                fullWidth
                variant="outlined"
                label={label}
                placeholder={placeholder || label}
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleOnBlur}
                onPaste={handlePaste} // Handle paste event
                error={!!errorMessage} // Show error only if there is an error message
                helperText={errorMessage ? errorMessage : ' '} // Show helper text only if there is an error
                InputProps={{
                    autoComplete: 'off',
                    startAdornment: emails.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {emails.map((email, index) => (
                                <Chip
                                    key={index}
                                    avatar={
                                        <Avatar
                                            sx={{
                                                margin: 0, width: 24, height: 24,
                                                fontSize: '14px', color: 'white',
                                                bgcolor: avatarColors[index % avatarColors.length],
                                            }}
                                        >
                                            <span style={{ color: 'white' }}>
                                                {extractAvatarInitials(email)}
                                            </span>
                                        </Avatar>
                                    }
                                    label={email}
                                    onDelete={() => handleDeleteEmail(email)}
                                    deleteIcon={<Cancel />}
                                    sx={{
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        border: '1px solid transparent',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                        },
                                        '&:focus': {
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                        },
                                        '&:hover .MuiSvgIcon-root': {
                                            color: '#d32f2f',
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                    ),
                }}
                sx={{
                    '.MuiInputBase-root': {
                        flexWrap: emails.length > 0 ? 'wrap' : 'nowrap',
                        alignItems: emails.length > 0 ? 'flex-start' : 'center',
                        paddingTop: emails.length > 0 ? '24px' : '0',
                    },
                    '.MuiFormHelperText-root': {
                        minHeight: errorMessage ? '1em' : '0',
                        visibility: errorMessage ? 'visible' : 'hidden',
                        display: errorMessage ? 'block' : 'none',
                    },
                }}
            />
        </Box>
    );
}